/** Mockup uzerine basilan renk adi ("Military Green"). Alici rengi model
 *  fotografindan tahmin ediyordu; etiket mockup basina kalici ayardir,
 *  alan belirleme penceresinde konum/boyut/stil duzenlenir. Sunucu render'i
 *  ve tarayici onizlemesi ayni geometriyi kullanir. */
export interface MockupColorLabel {
  enabled: boolean;
  /** Basilacak metin; bossa mockup'in renk alani */
  text?: string | null;
  /** Etiketin SOL UST kosesi, gorsele oranla (0-1) */
  x: number;
  y: number;
  /** Etiket yuksekligi, gorselin kisa kenarina oranla */
  size: number;
  /** Zemin: pill (yuvarlak), box (koseli), brush (firca darbesi), none (yalniz yazi) */
  shape: "pill" | "box" | "brush" | "none";
  bg: string;
  bgOpacity: number;
  textColor: string;
  uppercase: boolean;
}

export const DEFAULT_COLOR_LABEL: MockupColorLabel = {
  enabled: true, text: null, x: 0.03, y: 0.895, size: 0.075,
  shape: "pill", bg: "#ffffff", bgOpacity: 0.88, textColor: "#222222", uppercase: false,
};

const HEX = /^#[0-9a-f]{6}$/i;
const clamp = (n: unknown, lo: number, hi: number, d: number) => {
  const v = Number(n);
  return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d;
};

/** Gelen JSON'u guvenli etikete cevirir (eksik alan varsayilandan). null = kapali/yok */
export function normalizeColorLabel(raw: unknown): MockupColorLabel | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<MockupColorLabel>;
  const d = DEFAULT_COLOR_LABEL;
  return {
    enabled: r.enabled !== false,
    text: typeof r.text === "string" && r.text.trim() ? r.text.trim().slice(0, 40) : null,
    x: clamp(r.x, 0, 1, d.x),
    y: clamp(r.y, 0, 1, d.y),
    size: clamp(r.size, 0.02, 0.2, d.size),
    shape: r.shape === "box" || r.shape === "brush" || r.shape === "none" ? r.shape : "pill",
    bg: typeof r.bg === "string" && HEX.test(r.bg) ? r.bg : d.bg,
    bgOpacity: clamp(r.bgOpacity, 0, 1, d.bgOpacity),
    textColor: typeof r.textColor === "string" && HEX.test(r.textColor) ? r.textColor : d.textColor,
    uppercase: !!r.uppercase,
  };
}

export function colorLabelText(label: MockupColorLabel, mockupColor?: string | null): string {
  const t = (label.text || mockupColor || "").trim();
  return label.uppercase ? t.toUpperCase() : t;
}

/** Firca darbesi zemini (kutuya oranla noktalar): duz ust/alt, tirtikli uclar.
 *  Sunucu, Studio ve alan duzenleyici ayni sekli cizer. */
const BRUSH: Array<[number, number]> = [
  [0.03, 0.1], [0.2, 0.04], [0.45, 0.08], [0.7, 0.02], [0.96, 0.07],
  [0.99, 0.22], [0.95, 0.35], [1, 0.5], [0.96, 0.66], [0.99, 0.82],
  [0.94, 0.95], [0.7, 0.98], [0.45, 0.93], [0.22, 0.99], [0.04, 0.92],
  [0, 0.78], [0.04, 0.62], [0, 0.48], [0.03, 0.32], [0, 0.18],
];
/** w x h kutuda firca darbesi SVG yolu (kutunun sol ust kosesine gore) */
export function colorLabelBrushPath(w: number, h: number): string {
  return BRUSH.map(([px, py], i) => `${i ? "L" : "M"}${(px * w).toFixed(1)} ${(py * h).toFixed(1)}`).join(" ") + " Z";
}

/** Piksel geometrisi. textWidth(fontSize) = metnin o puntodaki genisligi (font olcumu). */
export function colorLabelBox(label: MockupColorLabel, W: number, H: number, textWidth: (fontSize: number) => number) {
  const h = Math.max(8, Math.min(W, H) * label.size);
  const fontSize = h * 0.48;
  const pad = label.shape === "none" ? 0 : h * 0.55;
  const w = textWidth(fontSize) + pad * 2;
  // Kenardan tasmasin
  const x = Math.min(Math.max(0, label.x * W), Math.max(0, W - w));
  const y = Math.min(Math.max(0, label.y * H), Math.max(0, H - h));
  return { x, y, w, h, fontSize, pad, radius: label.shape === "pill" ? h / 2 : label.shape === "box" ? h * 0.12 : 0, baseline: y + h / 2 + fontSize * 0.36 };
}
